import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { ILLNESS_REPORTS_LIST, ILLNESS_REPORT_SIGNATURE } from '../../api/constants';

// Fetch illness reports list
export const fetchIllnessReports = createAsyncThunk(
  'illnessReport/fetchIllnessReports',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      
      const response = await apiHandler(ILLNESS_REPORTS_LIST, 'POST', formData);
      return response.data.data.data; // response.data.data.data contains { meta, data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch illness reports');
    }
  }
);

// Submit parent signature
export const submitParentSignature = createAsyncThunk(
  'illnessReport/submitParentSignature',
  async ({ reportId, parent_signature, parent_signature_date }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('id', reportId);
      formData.append('parent_signature', parent_signature);
      formData.append('parent_signature_date', parent_signature_date);
      
      const response = await apiHandler(ILLNESS_REPORT_SIGNATURE, 'POST', formData);
      return { reportId, data: response.data.data.FormAdministrationMedication };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit signature');
    }
  }
);

const illnessReportSlice = createSlice({
  name: 'illnessReport',
  initialState: {
    reports: [],
    meta: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch illness reports
      .addCase(fetchIllnessReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIllnessReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchIllnessReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit parent signature
      .addCase(submitParentSignature.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitParentSignature.fulfilled, (state, action) => {
        state.submitLoading = false;
        // Update the report in the list with the new signature
        const reportIndex = state.reports.findIndex(r => r.id === action.payload.reportId);
        if (reportIndex !== -1) {
          state.reports[reportIndex].parent_signature = action.payload.data.parent_signature;
          state.reports[reportIndex].parent_signature_date = action.payload.data.parent_signature_date;
        }
      })
      .addCase(submitParentSignature.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      });
  }
});

export const { clearErrors } = illnessReportSlice.actions;

// Selectors
export const selectReports = (state) => state.illnessReport.reports;
export const selectMeta = (state) => state.illnessReport.meta;
export const selectLoading = (state) => state.illnessReport.loading;
export const selectError = (state) => state.illnessReport.error;
export const selectSubmitLoading = (state) => state.illnessReport.submitLoading;
export const selectSubmitError = (state) => state.illnessReport.submitError;

export default illnessReportSlice.reducer;
